import { useCallback, useState } from 'react';
import { useWebsiteReferralTracking } from '~/modules/tracking/util/websiteTracking';
import { useWebsiteVisited } from '~/modules/tracking/hook/useWebsiteVisited';
import { useEnquirySubmitted } from '~/modules/tracking/hook/useEnquirySubmitted';
import { Button } from '@rocco/components/button';
import {
    Download,
    ExternalLink,
    MessageCircleIcon,
    Package,
} from 'lucide-react';
import ActionCompletedIcon from '~/modules/tracking/component/ActionCompletedIcon';
import { useEnquiryModal } from '~/modules/enquiry/hooks/use-enquiry-modal';
import type { DropdownItem } from '~/modules/enquiry/types';
import type { Branch } from '~/modules/enquiry/util/extract-branch-from-network.server';
import type LibraryDownload from '@modules/product/type/library-download';
import { lazy, Suspense } from 'react';
import { useHydrated } from 'remix-utils';
import { cn } from '@rocco/utils/cn';
import { useProductFilesModalDeepLink } from '@modules/product/hook/use-product-files-modal-deep-link';
import { useProductFilesModalReopen } from '@modules/product/hook/use-product-files-modal-reopen';

/** Lazy-loaded ProductFilesModal to reduce initial bundle size. */
const ProductFilesModal = lazy(
    () => import('@modules/product/component/downloads/ProductFilesModal'),
);

interface ProductDetailsCTAProps {
    /** The product page ID. */
    pageID: number;
    /** The product title/name. Used to pre-fill the enquiry message. */
    productTitle: string;
    /** The professional's website URL or product-specific website URL. */
    website?: string | null;
    /** The professional's ID. */
    professionalID: number;
    /** The professional's title/name. */
    professionalTitle: string;
    /** The CTA label from the professional's settings (e.g., "Request Price / Brochure"). */
    primaryCTALabel: string;
    /** Branch options for the enquiry modal dropdown. */
    branchOptions: DropdownItem<Branch>[];
    /** The professional's company logo URL. */
    companyLogo: string;
    /** The professional's logo background colour. */
    logoBackgroundColor: string;
    /** The first product image URL. Shown in the enquiry modal header. */
    productImage?: string;
    /** Whether to show the enquiry CTA button. Defaults to true. */
    showEnquiryCTA?: boolean;
    /** Whether the product has samples available for request. */
    samplesAvailable?: boolean;
    /** The list of library downloads to display in the spec files modal. */
    libraryDownloads: LibraryDownload[];
    /** The professional's custom enquiry message to display above the enquiry form. */
    customEnquiryMessage?: string;
    /** Whether the professional has enabled the enquiry message box override. */
    enquiryMessageOverrideEnabled?: boolean;
    /** The professional's override for the enquiry message box placeholder. */
    enquiryMessagePlaceholder?: string;
}

/**
 * CTA buttons displayed at the bottom of the product details section.
 * Shows "Visit Website", "Spec Files" (if downloads exist), and enquiry buttons
 * matching the style of the page header CTAs.
 */
const ProductDetailsCTA = ({
    pageID,
    productTitle,
    website,
    professionalID,
    professionalTitle,
    primaryCTALabel,
    branchOptions,
    companyLogo,
    logoBackgroundColor,
    productImage,
    showEnquiryCTA = true,
    samplesAvailable = false,
    libraryDownloads,
    customEnquiryMessage,
    enquiryMessageOverrideEnabled,
    enquiryMessagePlaceholder,
}: ProductDetailsCTAProps) => {
    const [productFilesModalOpen, setProductFilesModalOpen] = useState(false);
    const hydrated = useHydrated();

    const { referralTrackingURL, referralTrackingParams } =
        useWebsiteReferralTracking(website, {
            libraryItemID: pageID,
            professionalID: professionalID,
            professionalTitle,
            source: 'ProductDetailsCTA',
        });

    const { hasVisited, markVisited } = useWebsiteVisited(pageID);
    const { hasSubmitted } = useEnquirySubmitted(pageID);

    const { openEnquiryModal } = useEnquiryModal({
        professional: {
            ID: professionalID,
            Title: professionalTitle,
            branchOptions,
            CompanyLogo: companyLogo,
            LogoBackgroundColor: logoBackgroundColor,
            CustomEnquiryMessage: customEnquiryMessage,
            EnquiryMessageOverrideEnabled: enquiryMessageOverrideEnabled,
            EnquiryMessagePlaceholder: enquiryMessagePlaceholder,
        },
        itemType: 'product',
        siteTreeID: pageID,
        source: 'ProductDetailsCTA',
        ctaLabel: primaryCTALabel,
        includeBranch: branchOptions.length > 0,
        itemTitle: productTitle,
        itemImage: productImage,
    });

    /** Opens the enquiry modal pre-filled for a specific file. */
    const handleEnquireAboutFile = useCallback(
        (fileTitle: string) => {
            openEnquiryModal({
                title: `Enquire about ${fileTitle}`,
                ctaLabel: `Enquire about ${fileTitle}`,
                messageSubject: fileTitle,
            });
        },
        [openEnquiryModal],
    );

    // Filter to only valid downloads (have a link)
    const validDownloads = libraryDownloads.filter(
        (d) => d.InternalLink || d.ExternalLink,
    );
    const hasDownloads = validDownloads.length > 0;

    useProductFilesModalReopen({
        productID: pageID,
        professionalID,
        enabled: hasDownloads,
        onReopen: () => setProductFilesModalOpen(true),
    });

    // Legacy still owns the files popup. Revamp scrolls to Downloads
    // instead; this page is the popup path for `#product-files`.
    useProductFilesModalDeepLink({
        enabled: hasDownloads,
        open: productFilesModalOpen,
        onOpen: () => setProductFilesModalOpen(true),
    });

    // Don't render anything if there's nothing to show
    if (!referralTrackingURL && !showEnquiryCTA && !hasDownloads) {
        return null;
    }

    const buttonStyles = 'min-w-61.25 rounded-[5px] px-8 py-4 uppercase';

    /** Wrapper handles the lift + shadow so the Button's internal CSS is untouched. */
    const liftWrapperBase = cn(
        'relative top-0 inline-block',
        'rounded-[5px] transition-[top,box-shadow] duration-200 ease-out',
        'active:top-0 active:duration-75',
    );
    const secondaryLift = cn(
        liftWrapperBase,
        'hover:-top-0.5 hover:shadow-[0_4px_12px_0_rgba(0,0,0,0.1)]',
        'active:shadow-[0_1px_3px_0_rgba(0,0,0,0.08)]',
    );
    const primaryLift = cn(
        liftWrapperBase,
        'hover:-top-0.5 hover:shadow-[0_4px_16px_0_rgba(0,0,0,0.2)]',
        'active:shadow-[0_2px_6px_0_rgba(0,0,0,0.15)]',
    );

    const iconStyles = 'ml-2 h-4 w-4';

    return (
        <>
            <div className="flex flex-wrap gap-4 py-8">
                {referralTrackingURL && (
                    <span className={secondaryLift}>
                        <Button
                            color="secondary"
                            weight="default"
                            size="larger"
                            className={buttonStyles}
                            asChild
                        >
                            <a
                                href={referralTrackingURL}
                                target="_blank"
                                {...referralTrackingParams}
                                rel="noopener"
                                onClick={markVisited}
                            >
                                Visit Website
                                <ActionCompletedIcon
                                    isCompleted={hasVisited}
                                    defaultIcon={
                                        <ExternalLink className={iconStyles} />
                                    }
                                    className={iconStyles}
                                />
                            </a>
                        </Button>
                    </span>
                )}
                {hasDownloads && (
                    <span className={secondaryLift}>
                        <Button
                            color="secondary"
                            weight="default"
                            size="larger"
                            className={buttonStyles}
                            onClick={() => setProductFilesModalOpen(true)}
                        >
                            Product Files ({validDownloads.length})
                            <Download className={iconStyles} />
                        </Button>
                    </span>
                )}
                {samplesAvailable && showEnquiryCTA && (
                    <span className={secondaryLift}>
                        <Button
                            color="secondary"
                            weight="default"
                            size="larger"
                            className={buttonStyles}
                            onClick={() =>
                                openEnquiryModal({
                                    title: 'Request a Sample',
                                    ctaLabel: 'Request a Sample',
                                    messageSubject: `a sample of ${productTitle}`,
                                })
                            }
                        >
                            Request a Sample
                            <Package className={iconStyles} />
                        </Button>
                    </span>
                )}
                {showEnquiryCTA && (
                    <span className={primaryLift}>
                        <Button
                            color="dark"
                            weight="default"
                            size="larger"
                            className={buttonStyles}
                            onClick={() =>
                                openEnquiryModal({
                                    title: primaryCTALabel,
                                    ctaLabel: primaryCTALabel,
                                })
                            }
                        >
                            {primaryCTALabel}
                            <ActionCompletedIcon
                                isCompleted={hasSubmitted}
                                defaultIcon={
                                    <MessageCircleIcon className={iconStyles} />
                                }
                                className={iconStyles}
                            />
                        </Button>
                    </span>
                )}
            </div>
            {hasDownloads && hydrated && (
                <Suspense fallback={null}>
                    <ProductFilesModal
                        open={productFilesModalOpen}
                        onOpenChange={setProductFilesModalOpen}
                        libraryDownloads={validDownloads}
                        productID={pageID}
                        professionalID={professionalID}
                        professionalTitle={professionalTitle}
                        productImage={productImage}
                        onEnquireAboutFile={handleEnquireAboutFile}
                    />
                </Suspense>
            )}
        </>
    );
};

export default ProductDetailsCTA;
